import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { SecurityService } from '../security/security.service';
import {
  DEMO_BACKEND_ENABLED,
  INVENTED_DATA_ENABLED,
  fillPartner,
  fillPerson,
  fillProspect,
  fillTask,
  forgetMyRating,
  noteDemoData,
  rememberMyRating,
  rememberPerson,
  seedDirectoryLookup,
  seedOrganizationOffers,
  seedOrganizationRatings,
  seedOrganizationReviews,
  seedPeopleCapabilities,
  seedPeoplePermissions,
  seedPeopleRatings,
} from './directory-demo-backend';

/**
 * Stands in for the directory endpoints `backend/api` master does not have yet.
 *
 * The reasoning, the measurements and every seed live in
 * {@link ./directory-demo-backend.ts}. This file is only the wiring, and it does exactly
 * two things:
 *
 * 1. **A 404 becomes a seeded answer.** The real request still goes out. Only when the
 *    server says "no such endpoint" is an invented reply substituted — so the day David
 *    deploys one, it starts working with no change here. Any other failure (401, 403, 429,
 *    500) is passed through untouched: those come from an endpoint that exists, and
 *    swallowing them would hide a real fault.
 * 2. **A missing field is filled in.** For endpoints that DO answer but carry fewer fields
 *    than the page reads, each absent field is filled and each present one is left alone.
 *
 * **To remove it:** delete this folder and the word `directoryDemoBackendInterceptor` from
 * the `withInterceptors([...])` list in `app.config.ts`. Nothing else refers to it.
 */

/**
 * The only page this interceptor may touch.
 *
 * Three of the endpoints it fills — `/people/directory`, `/people/{id}`, `/peopleTasks/{id}` — are
 * asked for by screens all over the app: the assignee picker inside every create form, My
 * Organization's team list, the previous directory at `/directories_old`. Matching on the URL alone
 * reached all of them, so they printed invented working hours, invented links and "Project 40213"
 * with nothing on the page to say the data was not real. Only the rebuilt directory carries the
 * "Demo data" pill, so only the rebuilt directory may be filled.
 *
 * ⚠ The rebuilt page took this path from the old one on 18 Sep 2026. `startsWith` alone is no
 * longer safe: `/directories_old` starts with `/directories` too, and would have been filled with
 * demo data by the very guard written to keep it out. Match the segment, not the prefix.
 */
const DEMO_ROUTE = '/directories';

/** True only on the rebuilt directory itself — never on `/directories_old`. */
function onDemoRoute(routerUrl: string): boolean {
  const path = routerUrl.split(/[?#]/)[0];
  return path === DEMO_ROUTE || path.startsWith(`${DEMO_ROUTE}/`);
}

/** Path suffixes this interceptor is allowed to touch. Anything else passes straight through. */
function classify(req: HttpRequest<unknown>): string | null {
  // Compare on the path alone: the base URL differs per environment, and a query string
  // would defeat a plain endsWith.
  let path: string;
  try {
    path = new URL(req.url, window.location.origin).pathname;
  } catch {
    path = req.url.split('?')[0];
  }

  // ── Endpoints that do not exist on master (verified: all answer 404) ──
  if (path.endsWith('/ratings/organizations')) return 'ratings';
  if (path.endsWith('/ratings/people')) return 'peer-ratings';
  if (/\/ratings\/organizations\/\d+\/reviews$/.test(path)) return 'reviews';
  if (/\/ratings\/organizations\/\d+\/mine$/.test(path)) return 'my-rating';
  if (path.endsWith('/organizationOffers')) return 'offers';
  if (path.endsWith('/peoplePermissions')) return 'permissions';
  if (path.endsWith('/peopleCapabilities')) return 'capabilities';
  if (path.endsWith('/directoryLookup')) return 'lookup';

  // ── Endpoints that answer, but with fields master does not select ────
  // /people/directory is tested first: it would otherwise match the /people/{id} shape.
  if (path.endsWith('/people/directory')) return 'fill-directory';
  if (/\/people\/\d+$/.test(path)) return 'fill-person';
  if (/\/administration\/organizations\/\d+\/getPartners$/.test(path)) return 'fill-partners';
  if (/\/peopleTasks\/\d+$/.test(path)) return 'fill-tasks';
  if (path.endsWith('/marketplace/prospects')) return 'fill-prospects';

  return null;
}

/** The numeric ids in a comma-separated query parameter, as the batch endpoints take them. */
function idsFrom(req: HttpRequest<unknown>, param: string): number[] {
  return (req.params.get(param) ?? '')
    .split(',')
    .map(part => Number(part.trim()))
    .filter(id => Number.isFinite(id) && id > 0);
}

/** The id in a path like `/ratings/organizations/148/reviews`. */
function idIn(url: string): number {
  const match = url.match(/\/organizations\/(\d+)\//);
  return match ? Number(match[1]) : 0;
}

/** A 200 carrying `body`, shaped like the response the real endpoint would have sent. */
function answer(req: HttpRequest<unknown>, body: any, what: string): Observable<HttpEvent<unknown>> {
  noteDemoData(what);
  return of(new HttpResponse({ status: 200, body, url: req.url }));
}

export const directoryDemoBackendInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (!DEMO_BACKEND_ENABLED) return next(req);

  const kind = classify(req);
  if (!kind) return next(req);

  // inject() has to happen here, in the injection context — not inside a pipe callback,
  // where it throws NG0203. The same rule the auth interceptor above it obeys.
  const router = inject(Router);
  // Scoped to the one page that says it is showing demo data. See DEMO_ROUTE.
  if (!onDemoRoute(router.url)) return next(req);

  const security = inject(SecurityService);
  const readerOrgId = Number(security.securityObject?.orgId) || 0;
  const readerPeopleId = Number(security.securityObject?.userId) || 0;

  if (kind.startsWith('fill-')) {
    return next(req).pipe(
      map(event => (event instanceof HttpResponse ? fill(event, kind, readerOrgId, readerPeopleId) : event)),
    );
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // 404 means "no such endpoint on this build". Anything else is a real answer from a
      // real endpoint and belongs to the caller.
      if (error.status !== 404) return throwError(() => error);
      // Real data only: the lookup is the one stand-in that answers from real rows (see
      // seedDirectoryLookup); every other missing endpoint keeps its 404 and the page shows
      // its empty state.
      if (!INVENTED_DATA_ENABLED && kind !== 'lookup') return throwError(() => error);
      return standIn(req, kind);
    }),
  );
};

/** The seeded reply for an endpoint that answered 404. */
function standIn(req: HttpRequest<unknown>, kind: string): Observable<HttpEvent<unknown>> {
  switch (kind) {
    case 'ratings':
      return answer(req, seedOrganizationRatings(idsFrom(req, 'organizationIds')), 'partner ratings');

    case 'peer-ratings':
      return answer(req, seedPeopleRatings(idsFrom(req, 'peopleIds')), 'peer ratings');

    case 'reviews':
      return answer(req, seedOrganizationReviews(idIn(req.url)), 'partner ratings');

    case 'my-rating': {
      // The form has to behave like a form. The write is remembered in the seed module and
      // read back by the reviews stand-in, so a saved star stays on screen — and it is never
      // sent anywhere. 204, matching what the real endpoint returns.
      const organizationId = idIn(req.url);
      if (req.method === 'DELETE') forgetMyRating(organizationId);
      else rememberMyRating(organizationId, req.body);
      noteDemoData('partner ratings');
      return of(new HttpResponse({ status: 204, body: null, url: req.url }));
    }

    case 'offers':
      return answer(req, seedOrganizationOffers(idsFrom(req, 'organizationIds')), 'organization offers');

    case 'capabilities':
      return answer(req, seedPeopleCapabilities(idsFrom(req, 'peopleIds')), 'what people can do');

    case 'permissions':
      return answer(req, seedPeoplePermissions(idsFrom(req, 'peopleIds')), 'permissions');

    case 'lookup': {
      const result = seedDirectoryLookup(req.params.get('email') ?? '');
      // Without inventions the answer is a real row or "no account": nothing to label as demo.
      if (!INVENTED_DATA_ENABLED) return of(new HttpResponse({ status: 200, body: result, url: req.url }));
      return answer(req, result, 'email lookup');
    }

    default:
      return of(new HttpResponse({ status: 200, body: {}, url: req.url }));
  }
}

/** Fills the fields an answering endpoint left out, and leaves every field it sent alone. */
function fill(
  response: HttpResponse<unknown>,
  kind: string,
  readerOrgId: number,
  readerPeopleId: number,
): HttpResponse<unknown> {
  const body: any = response.body;
  if (!body || typeof body !== 'object') return response;

  // Both list shapes in use: a DataTables envelope, and a plain array.
  const rows: any[] = Array.isArray(body) ? body : Array.isArray(body.data) ? body.data : [];
  let touched = false;

  switch (kind) {
    case 'fill-directory':
      for (const person of rows) {
        // Real rows, remembered against their real addresses, so Add contact can answer
        // truthfully about anybody already on this page.
        rememberPerson(person, readerOrgId, readerPeopleId);
        // Hours, links, status messages and reporting lines are all invented.
        if (INVENTED_DATA_ENABLED) touched = fillPerson(person, rows) || touched;
      }
      if (touched) noteDemoData('working hours, links and reporting lines');
      break;

    case 'fill-person':
      if (!INVENTED_DATA_ENABLED) break;
      touched = fillPerson(body, rows);
      if (touched) noteDemoData('working hours, links and reporting lines');
      break;

    case 'fill-partners':
      // Business hours and vacations are invented.
      if (!INVENTED_DATA_ENABLED) break;
      for (const org of rows) touched = fillPartner(org) || touched;
      if (touched) noteDemoData('organization business hours');
      break;

    case 'fill-prospects':
      // Only the credential, which is the server's own rule applied to the server's own data.
      // The other four list fields master omits are real facts about real people — see fillProspect.
      for (const prospect of rows) touched = fillProspect(prospect) || touched;
      break;

    case 'fill-tasks':
      // Not a seed: the real project id, printed because the server sends no name. See fillTask.
      for (const task of rows) touched = fillTask(task) || touched;
      break;
  }

  return response;
}
